import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { MediaAssetsState, MediaFilter, MediaFormData } from '../../../types/media.types';
import apiClient from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../api/endPoints';

const initialState: MediaAssetsState = {
  items: [],
  filteredItems: [],
  isLoading: false,
  error: null,
  filters: {
    publisherPK: '',
    category: '',
    searchTerm: '',
  },
  pagination: {
    page: 0,
    rowsPerPage: 10,
    totalCount: 0,
  },
};

export const fetchMediaAssets = createAsyncThunk('media/fetchMediaAssets', async () => {
  const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.MEDIA.LIST);
  return response.data.items || response.data;
});

export const fetchFilteredMediaAssets = createAsyncThunk(
  'media/fetchFilteredMediaAssets',
  async (filters: MediaFilter) => {
    const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.MEDIA.LIST, {
      params: filters,
    });
    return response.data.items || response.data;
  }
);

export const fetchMediaAssetsByPublisher = createAsyncThunk(
  'media/fetchMediaAssetsByPublisher',
  async (publisherId: string) => {
    const response = await apiClient.get(API_ENDPOINTS.PUBLISHER.MEDIA.LIST, {
      params: { publisherId },
    });
    return response.data.items || response.data;
  }
);

export const createMediaAsset = createAsyncThunk(
  'media/createMediaAsset',
  async (data: MediaFormData) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description);
    formData.append('publisherPK', data.publisherPK);
    formData.append('mimeType', data.mimeType);
    if (data.category) formData.append('category', data.category);
    if (data.keywords?.length) formData.append('keywords', JSON.stringify(data.keywords));
    if (data.file) formData.append('file', data.file);

    const response = await apiClient.post(API_ENDPOINTS.PUBLISHER.MEDIA.CREATE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }
);

export const updateMediaAsset = createAsyncThunk(
  'media/updateMediaAsset',
  async ({ id, data }: { id: string; data: Partial<MediaFormData> }) => {
    const formData = new FormData();
    if (data.name) formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    if (data.mimeType) formData.append('mimeType', data.mimeType);
    if (data.category) formData.append('category', data.category);
    if (data.keywords) formData.append('keywords', JSON.stringify(data.keywords));
    if (data.file) formData.append('file', data.file);

    const response = await apiClient.put(API_ENDPOINTS.PUBLISHER.MEDIA.UPDATE(id), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }
);

export const deleteMediaAsset = createAsyncThunk(
  'media/deleteMediaAsset',
  async (id: string) => {
    await apiClient.delete(API_ENDPOINTS.PUBLISHER.MEDIA.DELETE(id));
    return id;
  }
);

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<{ key: keyof MediaFilter; value: string }>) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
      state.pagination.page = 0;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.pagination.rowsPerPage = action.payload;
      state.pagination.page = 0;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMediaAssets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMediaAssets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchMediaAssets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch media assets';
      })
      
      .addCase(fetchFilteredMediaAssets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFilteredMediaAssets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchFilteredMediaAssets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch filtered media assets';
      })
      
      .addCase(fetchMediaAssetsByPublisher.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMediaAssetsByPublisher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filteredItems = action.payload;
        state.pagination.totalCount = action.payload.length;
      })
      .addCase(fetchMediaAssetsByPublisher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch media assets by publisher';
      })
      
      .addCase(createMediaAsset.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createMediaAsset.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items.push(action.payload);
        state.filteredItems.push(action.payload);
        state.pagination.totalCount = state.filteredItems.length;
      })
      .addCase(createMediaAsset.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create media asset';
      })
      
      .addCase(updateMediaAsset.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMediaAsset.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;
        const itemIndex = state.items.findIndex(item => item.id === updated.id);
        if (itemIndex !== -1) state.items[itemIndex] = updated;
        const filteredIndex = state.filteredItems.findIndex(item => item.id === updated.id);
        if (filteredIndex !== -1) state.filteredItems[filteredIndex] = updated;
      })
      .addCase(updateMediaAsset.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update media asset';
      })
      
      .addCase(deleteMediaAsset.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteMediaAsset.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedId = action.payload;
        state.items = state.items.filter(item => item.id !== deletedId);
        state.filteredItems = state.filteredItems.filter(item => item.id !== deletedId);
        state.pagination.totalCount = state.filteredItems.length;
      })
      .addCase(deleteMediaAsset.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to delete media asset';
      });
  },
});

export const { setFilter, setPage, setRowsPerPage, resetFilters } = mediaSlice.actions;
export default mediaSlice.reducer;
